/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2026
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { html, LitElement, TemplateResult } from 'lit';
import FocusMixin from '@carbon/web-components/es/globals/mixins/focus.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import styles from './lightbox-media-viewer.scss';

const { stablePrefix: c4dPrefix } = settings;

/**
 * Media viewer modal body.
 */
abstract class C4DLightboxMediaViewerBody extends FocusMixin(LitElement) {
  /**
   * @returns The description content.
   */
  abstract _renderDescription(): TemplateResult | string | void;

  /**
   * @returns The media content.
   */
  abstract _renderMedia(): TemplateResult | string | void;

  /**
   * @returns The title content.
   */
  abstract _renderTitle(): TemplateResult | string | void;

  /**
   * @returns The CTA content.
   */
  _renderCTAs(): TemplateResult | string | void {
    return html`<slot name="cta"></slot>`;
  }

  render() {
    return html`
      <div
        class="${c4dPrefix}--lightbox-media-viewer__container"
        part="container">
        <div class="${c4dPrefix}--lightbox-media-viewer__row" part="row">
          <div class="${c4dPrefix}--lightbox-media-viewer__media" part="media">
            ${this._renderMedia()}
          </div>
          <div
            class="${c4dPrefix}--lightbox-media-viewer__media-description"
            part="content-wrapper">
            <div
              class="${c4dPrefix}--lightbox-media-viewer__content"
              part="content">
              <div
                part="title"
                class="${c4dPrefix}--lightbox-media-viewer__content__title"
                data-autoid="${c4dPrefix}--lightbox-media-viewer__content__title">
                ${this._renderTitle()}
              </div>
              <div
                part="description"
                class="${c4dPrefix}--lightbox-media-viewer__content__desc"
                data-autoid="${c4dPrefix}--lightbox-media-viewer__content__desc">
                ${this._renderDescription()}
              </div>
              <div
                part="cta"
                class="${c4dPrefix}--lightbox-media-viewer__content__cta"
                data-autoid="${c4dPrefix}--lightbox-media-viewer__content__cta">
                ${this._renderCTAs()}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DLightboxMediaViewerBody;
